import type { ArReadinessReasonCode, TmsInvoiceRow, TmsInvoiceRowDocuments, TmsReadinessReason } from '@numeo/types-main-web-app'

/**
 * What a Ready-tab load's item offers, right in the chat (never a board link: an upload made there would not reach the chat).
 * A rate con is uploaded here (the load's rate and broker come from it); a missing rate is set through the agent, whose edit
 * card asks before it writes; a POD missing or refused on quality is uploaded; a POD on file but not accepted is reviewed and
 * accepted in the chat (the accept question shows the file and the findings), never uploaded again.
 */
export type RowAction =
    | { kind: 'upload_ratecon'; label: string }
    | { kind: 'set_rate'; label: string }
    | { kind: 'upload_pod'; label: string }
    | { kind: 'review_pod'; label: string }
    | { kind: 'invoice'; label: string }

// Lower is fixed first. A reason not listed sorts last and has no action in the chat.
const ORDER: Partial<Record<ArReadinessReasonCode, number>> = {
    missing_ratecon: 0,
    rate_required: 1,
    missing_pod: 2,
    poor_quality: 3,
    missing_pages: 3,
    pod_not_accepted: 4,
    route_mismatch: 4,
    reference_mismatch: 4,
    unsigned: 4,
}

const rank = (code: ArReadinessReasonCode) => ORDER[code] ?? 9

// The reasons the POD's check found: the agent shows the check before anything is accepted (FR-D6).
const POD_CHECK_CODES: ReadonlySet<ArReadinessReasonCode> = new Set(['pod_not_accepted', 'reference_mismatch', 'route_mismatch', 'unsigned'])

/**
 * A reason in two or three words, for the item's line: the full sentences ("The PO number on the POD does not match the load's
 * reference (POD 22155, load 2118345, …)") made a row a paragraph on stage. The sentence stays as the label's tooltip.
 */
export const REASON_LABEL: Record<ArReadinessReasonCode, string> = {
    missing_pod: 'POD missing',
    pod_not_accepted: 'POD not accepted',
    poor_quality: 'POD unreadable',
    missing_pages: 'POD pages missing',
    reference_mismatch: 'Reference mismatch',
    route_mismatch: 'Address mismatch',
    unsigned: 'POD not signed',
    missing_ratecon: 'Rate con missing',
    rate_required: 'Rate missing',
    finance_issue: 'Finance issue',
    already_invoiced: 'Already invoiced',
    invoice_not_found: 'Invoice not found',
    shared_draft: 'Shared draft invoice',
    not_marked_ready: 'Not marked ready',
}

/** The reasons in the order they are fixed. */
export const byFixOrder = (reasons: TmsReadinessReason[]): TmsReadinessReason[] => [...reasons].sort((a, b) => rank(a.code) - rank(b.code))

/** What the check found: a load that can be invoiced now carries it as warnings, one that cannot as reasons. */
export const findingsOf = (row: TmsInvoiceRow): TmsReadinessReason[] => byFixOrder(row.invoiceable ? (row.warnings ?? []) : (row.reasons ?? []))

/** The action a reason needs, or null when the chat has none for it. */
function actionOf(code: ArReadinessReasonCode, codes: ReadonlySet<ArReadinessReasonCode>): RowAction | null {
    if (code === 'missing_ratecon') return { kind: 'upload_ratecon', label: 'Upload rate con' }
    if (code === 'rate_required') return { kind: 'set_rate', label: 'Set rate' }
    if (code === 'missing_pod') return { kind: 'upload_pod', label: 'Upload POD' }
    // A POD refused on quality is replaced; when none is on file at all, the one upload is just "Upload POD".
    if (code === 'poor_quality' || code === 'missing_pages')
        return { kind: 'upload_pod', label: codes.has('missing_pod') ? 'Upload POD' : 'Upload a clearer POD' }
    if (POD_CHECK_CODES.has(code)) return { kind: 'review_pod', label: 'Review POD' }
    return null
}

/**
 * Every action a load's item offers at once, in the order its reasons are fixed, each once. A load that can be invoiced now
 * offers the invoice first, then what its warnings still need: a rate con or POD to upload (a factor asks for both) or a POD to
 * review. Its rate is already there, so a rate is never set on it.
 */
export function rowActions(row: TmsInvoiceRow): RowAction[] {
    if (!row.invoiceable && row.readiness !== 'not_ready') return []
    const fixes = reasonActions(findingsOf(row)).filter((action) => !row.invoiceable || action.kind !== 'set_rate')
    // A row whose rate con chip is missing is not offered an invoice: the rate con is the fix, and the chip shows the same fact.
    const invoiceable = row.invoiceable && row.documents?.rate_con !== false
    return invoiceable ? [{ kind: 'invoice', label: 'Invoice' }, ...fixes] : fixes
}

/** The fixes a load's readiness reasons need, in the order they are fixed, each once: what an invoice refusal offers. */
export function reasonActions(reasons: TmsReadinessReason[]): RowAction[] {
    const sorted = byFixOrder(reasons)
    const codes = new Set(sorted.map((reason) => reason.code))
    const actions: RowAction[] = []
    for (const reason of sorted) {
        const action = actionOf(reason.code, codes)
        if (action && !actions.some((known) => known.kind === action.kind)) actions.push(action)
    }
    return actions
}

/**
 * What a load's documents still need, rate con first: a missing rate con or POD is uploaded, a POD on file but not accepted is
 * reviewed (its check shows before it is accepted). A BOL is never asked: invoicing does not need one.
 */
export function documentActions(documents: TmsInvoiceRowDocuments | undefined): RowAction[] {
    if (!documents) return []
    const actions: RowAction[] = []
    if (!documents.rate_con) actions.push({ kind: 'upload_ratecon', label: 'Upload rate con' })
    if (documents.pod === 'none') actions.push({ kind: 'upload_pod', label: 'Upload POD' })
    if (documents.pod === 'on_file') actions.push({ kind: 'review_pod', label: 'Review POD' })
    return actions
}

/**
 * What an item shows in place of an action once the user did it there: only what the user did, never that the load is fixed,
 * since the item is a snapshot and is not read again.
 */
export const DONE_LABEL: Record<RowAction['kind'], string> = {
    upload_ratecon: 'Rate con uploaded',
    upload_pod: 'POD uploaded',
    set_rate: 'Rate sent to the agent',
    review_pod: 'Asked to show the POD',
    invoice: 'Asked to invoice',
}

const numbered = (loadNumbers: string[]) => loadNumbers.map((n) => `#${n}`).join(', ')

/**
 * The chat message that invoices these loads, in one call of the invoice card (one question for all of them). Sent only after the
 * user approves the invoices in the block (`InvoiceApproval`), never on the click of an invoice action.
 */
export const invoiceLoadsMessage = (loadNumbers: string[]): string => `Invoice loads ${numbered(loadNumbers)}`

/**
 * The chat message that follows up an unpaid invoice: the words of the send card's approval ("Follow up on invoice “IN-1001”"). The
 * card asks before anything is emailed to the broker, so the message only starts that question.
 */
export const followUpMessage = (invoiceNumber: string) => `Follow up on invoice ${invoiceNumber}`

/** The largest rate the field takes: a load's rate is never this much, so anything above it is a typo. */
const MAX_RATE_USD = 10_000_000

/**
 * The amount typed for a rate, in dollars and cents ("$1,250.50"), or null: plain digits with at most two decimals (no
 * exponent, nothing rounded away), from $0.01 up to under $10,000,000.
 */
export function parseRateAmount(value: string): number | null {
    const plain = value.replace(/[$,\s]/g, '')
    if (!/^\d+(\.\d{1,2})?$/.test(plain)) return null
    const amount = Number(plain)
    return amount >= 0.01 && amount < MAX_RATE_USD ? amount : null
}

/** The chat message that asks the agent to set the load's rate: its edit card asks before anything is written. */
export const setRateMessage = (loadNumber: string, amount: number) => `Set the Original Rate of #${loadNumber} to $${amount.toFixed(2).replace(/\.00$/, '')}`

/**
 * The fixes a load offers, whichever card drew it: its reasons' fixes and its documents' fixes, each kind once. A POD on file that is not
 * accepted is reviewed here whatever the row was read as (a re-read row carries the same documents), so the action never depends on the path.
 */
export function fixesOf(reasons: TmsReadinessReason[], documents: TmsInvoiceRowDocuments | undefined): RowAction[] {
    const actions = reasonActions(reasons)
    for (const action of documentActions(documents)) if (!actions.some((known) => known.kind === action.kind)) actions.push(action)
    return actions
}
